// ---------------------------------------------------------------------------
// checkout — Paddle Classic, as on the source site.
//
// The source loads cdn.paddle.com/paddle/paddle.js and renders each buy button
// as `<a class="paddle_button" data-product="…">`; the tier data imported into
// this theme carries the matching Classic product ids (six digits, not
// Billing's `pri_…`). base.html loads that script when Settings has a Paddle
// vendor id, and stamps the id onto <body>.
//
// Paddle Classic does the binding: `Paddle.Setup()` scans the document for
// `.paddle_button` and wires each one to its `data-product`. So the happy path
// here is one call, and the click handling below is only a fallback for a
// paddle.js that exposes `Checkout` but not `Setup`. Binding both ways would
// open the overlay twice.
//
// Everything degrades: no vendor id, no script tag; no script, no `Paddle`
// global and the buttons stay inert anchors pointing at `#!`; a tier with a
// real destination is rendered as a plain link by macros/pricing.html and never
// reaches this module at all.
//
// Note Paddle has been moving Classic to end-of-life in favour of Paddle
// Billing. Migrating means re-sourcing every price id from the Paddle
// dashboard, since Classic product ids do not carry over — a separate job.
// ---------------------------------------------------------------------------

type PaddleGlobal = {
	Setup?: (opts: { vendor: number }) => void;
	Checkout?: { open: (opts: Record<string, unknown>) => void };
};

const getPaddle = (): PaddleGlobal | undefined =>
	(window as unknown as { Paddle?: PaddleGlobal }).Paddle;

export const initCheckout = (): void => {
	const buttons = Array.from(
		document.querySelectorAll<HTMLElement>('.paddle_button[data-product]'),
	);
	if (!buttons.length) return;

	const paddle = getPaddle();
	if (!paddle) return;

	const vendor = Number.parseInt(document.body.dataset.paddleVendor ?? '', 10);

	if (paddle.Setup && !Number.isNaN(vendor)) {
		try {
			paddle.Setup({ vendor });
			return; // Setup bound the buttons; a second binding would double-open.
		} catch (err) {
			console.error('[theme] Paddle.Setup failed:', err);
		}
	}

	if (!paddle.Checkout) return;
	for (const button of buttons) {
		const product = button.dataset.product;
		if (!product) continue;
		button.addEventListener('click', (event) => {
			event.preventDefault();
			try {
				paddle.Checkout!.open({ product });
			} catch (err) {
				console.error('[theme] Paddle.Checkout failed:', err);
			}
		});
	}
};
